import { useQuery } from '@tanstack/react-query';
import { fmtDateTime } from '@/lib/dates';
import {
  CheckCircle2,
  Clock,
  Hourglass,
  Loader2,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import { binder, type ApprovalRoutingApprover } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { useAuth } from '@/contexts/AuthContext';
import { useDocusealSelfHeal } from '@/hooks/useDocusealSelfHeal';

/**
 * Sprint-15 — Multi-approver routing progress for a version.
 *
 * Renders the live state of every required approver:
 *   - waiting  (blocked on an earlier sequence step)
 *   - pending  (their task right now)
 *   - signed   (✓ done)
 *   - rejected (✗ declined)
 *   - skipped  (any-of rule satisfied without them)
 *
 * Used on the Document Detail panel for any version in `in_approval`
 * status so the coordinator can see whose action is blocking and the
 * sponsor monitor (later) can audit the chain visually.
 *
 * The header shows the mode + the overall progress fraction so the
 * panel is legible at a glance: "Sequential · 1 of 3 signed",
 * "Parallel · Any 2 of 3 · 1 of 2 needed", etc.
 */

export interface ApprovalProgressProps {
  binderId: string;
  documentId: string;
  versionId: string;
}

export function ApprovalProgress({ binderId, documentId, versionId }: ApprovalProgressProps) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['approval-routing', binderId, documentId, versionId],
    queryFn: () => binder.getApprovalRouting(binderId, documentId, versionId),
    // Poll while the version is in_approval — webhook updates happen
    // out-of-band, so a periodic refetch is the simplest way to see
    // signatures land.
    refetchInterval: 15_000,
  });

  // Self-heal against DocuSeal directly, same fallback SignApprovalDialog
  // uses for in-app signing. Without this, an approver who signs via the
  // EMAILED link (never opens the in-app sign dialog) only ever gets
  // synced if the DocuSeal webhook actually lands — and that webhook can
  // silently go stale (see services/eisf/signatureSync.ts). This panel is
  // what's actually on screen whenever anyone checks an in_approval
  // document's status, regardless of who signed or how, so it's the right
  // place to catch a signature the webhook missed.
  useDocusealSelfHeal(versionId, true);

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading approval progress…
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="rounded border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
        Couldn't load approval routing for this version.
      </div>
    );
  }

  const data = q.data;
  if (!data || data.approvers.length === 0) {
    return (
      <div className="rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted italic">
        No approval routing rows for this version. Either the envelope hasn't
        been initiated yet, or this version went through the legacy single-signer
        flow.
      </div>
    );
  }

  const signed = data.approvers.filter((a) => a.status === 'signed').length;
  const rejected = data.approvers.filter((a) => a.status === 'rejected').length;
  const k = data.approvers.length;
  const required =
    data.approval_mode === 'parallel' && data.any_of_threshold
      ? data.any_of_threshold
      : k;

  const modeBadge =
    data.approval_mode === 'sequential'
      ? { label: 'Sequential', cls: 'bg-amber-100 text-amber-800' }
      : data.approval_mode === 'parallel'
      ? {
          label: data.any_of_threshold
            ? `Parallel · Any ${data.any_of_threshold} of ${k}`
            : 'Parallel · All-of',
          cls: 'bg-violet-100 text-violet-800',
        }
      : { label: 'Single approver', cls: 'bg-blue-100 text-blue-800' };

  return (
    <div className="rounded border border-surface-border bg-white p-3">
      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${modeBadge.cls}`}>
          {modeBadge.label}
        </span>
        <span className="text-ink-muted">
          {signed} of {required} signed
          {rejected > 0 && (
            <span className="ml-1.5 text-danger-700">· {rejected} declined</span>
          )}
        </span>
      </div>
      <ul className="space-y-1.5">
        {data.approvers
          .slice()
          .sort((a, b) =>
            a.sequence_order !== b.sequence_order
              ? a.sequence_order - b.sequence_order
              : a.role.localeCompare(b.role),
          )
          .map((a) => (
            <ApproverRow key={a.routing_id} a={a} isViewerThisApprover={a.user_id === user?.id} />
          ))}
      </ul>
    </div>
  );
}

function ApproverRow({ a, isViewerThisApprover }: { a: ApprovalRoutingApprover; isViewerThisApprover: boolean }) {
  const meta = statusMeta(a.status);
  return (
    <li className="flex items-start justify-between gap-3 rounded border border-surface-border/60 bg-surface-muted/10 px-2.5 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-[10px] text-ink-muted">#{a.sequence_order}</span>
          <span className="truncate font-medium text-ink-primary">{a.user_name || a.user_email}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">{humanRole(a.role)}</span>
        </div>
        {/* Signature meaning ("I approve this document for use at my site.")
            is the attestation statement THAT approver is committing to — only
            meaningful (and only their business) to show to that same person,
            not to every viewer of the document. */}
        {isViewerThisApprover && (
          <div className="truncate text-[11px] text-ink-muted">
            {a.signature_meaning}
          </div>
        )}
        {a.signed_at && (
          <div className="text-[10px] text-ink-muted">
            Signed {fmtDateTime(a.signed_at)}
          </div>
        )}
        {a.rejected_at && a.rejection_reason && (
          <div className="text-[10px] text-danger-700">
            Declined: {a.rejection_reason}
          </div>
        )}
      </div>
      <span
        className={`flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}
        title={meta.title}
      >
        {meta.icon}
        {meta.label}
      </span>
    </li>
  );
}

function statusMeta(status: ApprovalRoutingApprover['status']): {
  label: string;
  cls: string;
  title: string;
  icon: React.ReactNode;
} {
  switch (status) {
    case 'signed':
      return {
        label: 'Signed',
        cls: 'bg-emerald-100 text-emerald-800',
        title: 'This approver has signed.',
        icon: <CheckCircle2 className="h-3 w-3" />,
      };
    case 'pending':
      return {
        label: 'Pending',
        cls: 'bg-yellow-100 text-yellow-800',
        title: 'Waiting on this approver to sign.',
        icon: <Clock className="h-3 w-3" />,
      };
    case 'waiting':
      return {
        label: 'Waiting',
        cls: 'bg-slate-100 text-slate-700',
        title: 'Blocked on an earlier sequence step.',
        icon: <Hourglass className="h-3 w-3" />,
      };
    case 'rejected':
      return {
        label: 'Declined',
        cls: 'bg-danger-100 text-danger-800',
        title: 'This approver declined.',
        icon: <XCircle className="h-3 w-3" />,
      };
    case 'skipped':
      return {
        label: 'Skipped',
        cls: 'bg-slate-100 text-slate-700',
        title: 'Any-of rule satisfied without this signer.',
        icon: <MinusCircle className="h-3 w-3" />,
      };
  }
}
